import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/apiService';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const CODE_LENGTH = 6;

const VerifyEmail = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const inputsRef = useRef([]);

  // Auto-send verification code on mount
  useEffect(() => {
    if (currentUser && !currentUser.emailVerified) {
      sendVerificationCode();
    } else if (currentUser && currentUser.emailVerified) {
      // Already verified, redirect to dashboard
      navigate('/dashboard');
    } else if (!currentUser) {
      // Not logged in, redirect to login
      navigate('/auth/login');
    }
  }, [currentUser, navigate]);

  const sendVerificationCode = async () => {
    if (!currentUser) return;
    
    setSending(true);
    setError(null);

    try {
      const token = await currentUser.getIdToken();
      await ApiService.sendVerificationCode(currentUser.email, token);
      setSuccess('Verification code sent to your email!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sending verification code:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow single digit
    if (!/^[0-9]?$/.test(value)) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    // Auto-focus next input
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    setVerifying(true);

    try {
      const code = codeDigits.join('');
      const token = await currentUser.getIdToken();
      
      const result = await ApiService.verifyCode(currentUser.email, code, token);
      
      if (result.success) {
        setSuccess('Email verified successfully!');
        
        // Wait a moment then redirect
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        setError(result.message || 'Invalid verification code');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setError('Invalid verification code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  if (!currentUser) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="flex items-center justify-center pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              {/* Email Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
              <p className="text-gray-600 mb-2">
                A verification code has been sent to
              </p>
              <p className="text-blue-600 font-semibold mb-6">
                {currentUser.email}
              </p>

              {/* Success Message */}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-600 text-sm">{success}</p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Enter the 6-digit code sent to your email
                  </label>
                  
                  {/* 6 Separate Input Boxes */}
                  <div className="flex justify-center gap-2 mb-6">
                    {codeDigits.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => inputsRef.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        disabled={verifying}
                        autoFocus={index === 0}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:bg-gray-100"
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={verifying || codeDigits.some(d => !d)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? 'Verifying...' : 'Verify Email'}
                </button>
              </form>

              {/* Resend Button */}
              <div className="mt-6">
                <button
                  onClick={sendVerificationCode}
                  disabled={sending}
                  className="w-full px-6 py-3 bg-white border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Resend Code'}
                </button>
              </div>

              {/* Logout Link */}
              <div className="mt-6 text-center">
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                >
                  Sign out and return to login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
